import Vue from 'vue'
import { http } from '@/services/http.js'

// Manage-sidebar "needs attention" counts, e.g. {upload: 3}.
//
// To add a new badge: register its query below, then dispatch
// badges/refresh with that key wherever the underlying data changes
// (an approve/reject/pay/process action, or a page visit). Components
// that use ApproveButton.vue get this for free — it refreshes using
// its own `api` prop as the key.
const QUERIES = {
  // matches the Uploads page's "New" tab: unprocessed portal uploads
  upload: () => http.get('api/upload/', {
    params: { approved__isnull: true, is_portal: true, limit: 1 },
  }).then(res => res.data?.count || 0),

  // only flag profiles that are actually reviewable — pending with no
  // sample images yet means there's nothing to approve
  'artist-profile': () => http.get('api/artist-profile/', {
    params: { approved__isnull: true, has_sample_images: true, limit: 1 },
  }).then(res => res.data?.count || 0),

  // only badge overdue payments
  'payment-due': () => http.get('api/art-usage/payment-due/')
    .then(res => countArtistsToFlag(res.data?.data || [])),

  image: () => http.get('/content/search/v3/', {
    params: { processed: 'false', published: 'true', limit: 0 },
  }).then(res => res.data?.total || 0),
}

// Artists are paid at the end of the month, don't nag until then
const PAYMENT_DAY_OF_MONTH = 25

// How many artists the Payments Due badge should count.
//
// Work shipped in a given month is paid around PAYMENT_DAY_OF_MONTH. So the badge
// cutoff is the 1st of this month once PAYMENT_DAY_OF_MONTH has passed, and the
// 1st of last month before that; anything shipped earlier is payable now,
// whether it's this cycle's work or an unpaid straggler from an older one. The
// page itself still lists and pays anything the endpoint returns, any time.
export function countArtistsToFlag(artists, now = new Date()) {
  const monthsBack = now.getDate() >= PAYMENT_DAY_OF_MONTH ? 0 : 1
  const payableBefore = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1)
  return artists.filter(artist =>
    artist.usages?.some(i => new Date(i.date_shipped) < payableBefore)
  ).length
}

const state = {
  counts: {},
}

const actions = {
  async refresh({ commit }, key) {
    const query = QUERIES[key]
    if (!query) return
    try {
      const count = await query()
      commit('setCount', { key, count })
    } catch {
      // badges are decorative — ignore failures
    }
  },
  refreshAll({ dispatch }) {
    return Promise.all(Object.keys(QUERIES).map(key => dispatch('refresh', key)))
  },
}

const mutations = {
  setCount(state, { key, count }) {
    Vue.set(state.counts, key, count)
  },
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
}
